import { markRaw } from 'vue'
import { ElMessageBox } from 'element-plus'
import { RefreshRight } from '@element-plus/icons-vue'
import { APP_VERSION } from '@/config/appVersion'
import { API_BASE_URL } from '@/config/constants'
import { checkAuthResponse, getRequestAuthToken } from '@/utils/authSession'

export const VERSION_MISMATCH_CODE = 40901
let refreshPrompt = null
let stopVersionMonitor = null

export function getAppVersionHeaders() {
  return { 'X-App-Client': 'web', 'X-App-Version': APP_VERSION }
}

export function handleVersionMismatch(data) {
  if (data?.code !== VERSION_MISMATCH_CODE) return null

  if (!refreshPrompt) {
    // Keep one prompt for concurrent requests, including while navigation starts.
    refreshPrompt = Promise.resolve().then(() => ElMessageBox.alert(
      '新版本已就绪。\n刷新页面，继续你的创作。',
      '页面有更新',
      {
        confirmButtonText: '刷新并继续',
        customClass: 'app-version-dialog',
        modalClass: 'app-version-overlay',
        confirmButtonClass: 'app-version-refresh',
        icon: markRaw(RefreshRight),
        center: true,
        // Image editors/previews use their own high stacking layers.
        zIndex: 2147483000,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        closeOnHashChange: false
      }
    )).then(() => window.location.reload()).catch(() => {
      refreshPrompt = null
    })
  }

  const error = new Error('\u9875\u9762\u6709\u66f4\u65b0\uff0c\u8bf7\u5237\u65b0')
  error.code = VERSION_MISMATCH_CODE
  error.isVersionMismatch = true
  return error
}

// App owns the monitor, so it survives route switches and covers pages with no API activity.
export function startAppVersionMonitor(router) {
  if (stopVersionMonitor) return stopVersionMonitor
  let stopped = false
  let inFlight = false
  let controller = null
  const check = async () => {
    if (stopped || document.hidden || inFlight || refreshPrompt) return
    inFlight = true
    controller = new AbortController()
    const timeout = setTimeout(() => controller?.abort(), 10000)
    try {
      const response = await fetch(`${API_BASE_URL}/api/frontend/version`, {
        headers: getAppVersionHeaders(), cache: 'no-store', credentials: 'omit', signal: controller.signal
      })
      if ((!response.ok && response.status !== 409) || stopped) return
      const body = await response.json()
      if (stopped) return
      if (body?.code === VERSION_MISMATCH_CODE) {
        handleVersionMismatch(body)
      } else if (body?.code === 200 && body.data?.enabled === true
          && typeof body.data.requiredVersion === 'string' && body.data.requiredVersion.trim()
          && body.data.requiredVersion !== APP_VERSION) {
        handleVersionMismatch({ code: VERSION_MISMATCH_CODE })
      }
    } catch {
      // An offline tab or a backend being deployed is not evidence of an outdated client.
    } finally {
      clearTimeout(timeout)
      controller = null
      inFlight = false
    }
  }
  const removeRouteHook = router.afterEach(check)
  document.addEventListener('visibilitychange', check)
  window.addEventListener('focus', check)
  const timer = setInterval(check, 15000)
  stopVersionMonitor = () => {
    if (stopped) return
    stopped = true
    clearInterval(timer)
    controller?.abort()
    removeRouteHook()
    document.removeEventListener('visibilitychange', check)
    window.removeEventListener('focus', check)
    stopVersionMonitor = null
  }
  void check()
  return stopVersionMonitor
}

async function checkResponse(response) {
  let data = response?.data
  // Image download requests also receive the JSON error as a Blob.
  if (response?.status === 409 && typeof Blob !== 'undefined' && data instanceof Blob) {
    try { data = JSON.parse(await data.text()) } catch { return }
  }
  const error = handleVersionMismatch(data)
  if (error) throw error
}

export function installAppVersionGuard(client) {
  Object.assign(client.defaults.headers.common, getAppVersionHeaders())
  // Install before business interceptors so version errors take precedence.
  client.interceptors.response.use(async response => {
    await checkResponse(response)
    return response
  }, async error => {
    await checkResponse(error.response)
    throw error
  })
  return client
}

// Only use for our API, never for third-party signed uploads or media URLs.
export async function appVersionFetch(url, options = {}) {
  const headers = new Headers(options.headers)
  Object.entries(getAppVersionHeaders()).forEach(([key, value]) => headers.set(key, value))
  const response = await fetch(url, { ...options, headers })
  let data
  if (response.status === 401 || /json/i.test(response.headers.get('content-type') || '')) {
    try { data = await response.clone().json() } catch { /* HTTP 401 still invalidates the session. */ }
    await checkAuthResponse({ status: response.status, data }, getRequestAuthToken(headers), options.silent)
  }
  if (response.status === 409) {
    let data
    try { data = await response.clone().json() } catch { return response }
    const error = handleVersionMismatch(data)
    if (error) throw error
  }
  return response
}
